import { ToolAttributes } from "@nova/constants"
import { Icon } from "@nova/protobuf"
import { ComponentProps } from "react"
import { Map } from "react-map-gl"
import { LayerStatus } from "../layers/overlays/layerStatus"

export type MapStyleStateAttributes = {
    url: string | object
    name: string
}

// "partial" so it is not to be confused with the full DeckGl state we get on update rerenders
export type PartialViewState = {
    longitude: number
    latitude: number
    zoom: number
    pitch: number
    bearing: number
    width?: number
    height?: number
    // if more is needed, this is what DeckGl gives us:
    // latitude(pin):50.81339701183257
    // longitude(pin):-120.08305145866976
    // zoom(pin):5.593346896221357
    // bearing(pin):0
    // pitch(pin):0
    // altitude(pin):1.5
    // maxZoom(pin):20
    // minZoom(pin):0
    // maxPitch(pin):60
    // minPitch(pin):0
    // normalize(pin):true
    // position: [0, 0, 0]

    // somethings we also get...
    // transitionDuration(pin):1
    // transitionInterpolator: {...not serializable for storing in redux!!!}
}

type FireId = string

/** The timeline's extent is NOT stored here — it is a pure function of the wall clock and the
 *  published forecast run, derived by `useWeatherTimelineWindow`. Only the playhead persists. */
export type WeatherTimelineState = {
    /** ISO UTC — the continuous playhead. Layers interpolate between frames around it. */
    currentTime: string
    isPlaying: boolean
    /** Hours of weather-data per real second during playback. */
    playbackSpeed: number
}

export type WeatherMode = "wind" | "precipitation" | "lightning" | "radar"

export type WeatherPanelState = {
    mode: WeatherMode
    /** 0–1 opacity applied to the active weather layer. Default 0.75. */
    opacity: number
    /** Wind particle trails (heatmap still shows when off). Not persisted to localStorage. */
    particleEffect: boolean
    /** Ground (cloud-to-ground) lightning strikes. Filtered client-side. */
    groundStrikes: boolean
    /** GLM flash-event strikes. Filtered client-side. */
    flashStrikes: boolean
    /** Detailed-fetch area disks (red circles). Hides only the disk, not the flash-coverage logic. */
    detailedAreas: boolean
}

export enum WindUnit {
    Knots = "kt",
    MetersPerSecond = "m/s",
    MilesPerHour = "mph",
    KilometersPerHour = "km/h",
}

export enum PrecipUnit {
    Millimeters = "mm",
    Inches = "in",
}

/** Lightning forecast flash-density rate. Imperial uses square miles in place of km². */
export enum LightningUnit {
    PerSquareKilometerHour = "/km²/hr",
    PerSquareMileHour = "/mi²/hr",
}

/** Unit config the user sets in the weather menu; persisted to localStorage ("weatherPreferences"). */
export type WeatherPreferences = {
    windUnit: WindUnit
    precipUnit: PrecipUnit
    lightningUnit: LightningUnit
}

export type WeatherState = {
    /** Weather overlay active. Not persisted — always resets off on project load. */
    enabled: boolean
    timeline: WeatherTimelineState
    /** Preserved across project loads, not persisted to localStorage. */
    panel: WeatherPanelState
    /** Preserved across project loads and persisted to localStorage. */
    preferences: WeatherPreferences
}

export type ControlSliceState = {
    entities: {
        chapterId?: string
        fireId?: string
        flightId?: string
        hotspotId?: string
        firePerimeterId?: string
        selectedMapIcon?: Icon
        clipboardMapIcon?: ToolAttributes
    }
    history: {
        chapter: string[]
        viewStates: Record<FireId, PartialViewState>
    }
    viewState?: PartialViewState
    controllerOverrides: Partial<Pick<ComponentProps<typeof Map>, "dragPan" | "dragRotate">>
    isPictureInPicture?: boolean
    mapFeatures: {
        mapLabels: boolean
        contours: boolean
        shading: boolean
        buildings: boolean
    }
    mouse?: number[]
    layerDropdownOpen: boolean
    weather: WeatherState
    overlayStatus: Partial<Record<OverlayKey, LayerStatus>>
    overlaySceneTimes: Partial<Record<OverlayKey, number>>
}

export const overlayKeys = [
    "heat",
    "firePerimeters",
    "fireDanger",
    "elevation",
    "civilization",
    "geostationary",
] as const
export type OverlayKey = (typeof overlayKeys)[number]
